import type { BlocksContent } from "@strapi/blocks-react-renderer";
import clsx from "clsx";
import { type ComponentProps, useMemo } from "react";

import { CommonBlocksRenderer } from "@widgets/common-blocks-renderer";

import type { TicketType } from "@features/ticket-card/model";

import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Icon } from "@shared/ui/icon";
import { StyledButton } from "@shared/ui/styled-button";
import { Body, Heading } from "@shared/ui/typography";
import { formatNumberWithSpaces } from "@shared/utils/string";

import s from "./popup-content.module.scss";

export type PopupProps = ComponentProps<"div"> & {
  className?: string;
  data: TicketType;
  onBuyClick?: () => void;
  onClose?: () => void;
};

export const PopupContent = (props: PopupProps) => {
  const { data, onBuyClick, onClose } = props;

  const hasDetails = useMemo(() => {
    return (
      data?.moreDetails &&
      data?.moreDetails.length > 0 &&
      data?.moreDetails
        ?.map((item) =>
          item?.children.filter((child) => child?.type !== "list-item"),
        )
        .filter((child) => child?.length! > 0)
        .flat(1)
        .filter((child) => child?.text?.length! > 0).length > 0
    );
  }, [data]);

  if (!data) return;

  return (
    <div className={s.root}>
      <StyledButton
        onClick={onClose}
        size="l"
        className={s.cross}
        aria-label="Закрыть"
      >
        <Icon name="cross" />
      </StyledButton>
      <div className={s.text}>
        {data.shortTitle && (
          <Heading level={5} tag="h2" className={s.title}>
            {data.shortTitle}
          </Heading>
        )}
        {hasDetails ? (
          <div className={clsx(s.detailsWrapper, "lenis-prevent")}>
            <CommonBlocksRenderer data={data.moreDetails as BlocksContent} />
          </div>
        ) : (
          <div className={clsx(s.detailsWrapper, "lenis-prevent")}>
            <Body size="caption" weight="semiBold">
              {data.description}
            </Body>
          </div>
        )}
      </div>
      {data.price &&
      ((data.price.adult &&
        typeof data.price.adult === "object" &&
        data.price.adult.newPrice !== null &&
        data.price.adult.newPrice !== undefined) ||
        (data.price.childen &&
          typeof data.price.childen === "object" &&
          data.price.childen.newPrice !== null &&
          data.price.childen.newPrice !== undefined)) ? (
        <div className={s.price}>
          <div className={s.wrapper}>
            {data.price.adult &&
              typeof data.price.adult === "object" &&
              data.price.adult.newPrice !== null &&
              data.price.adult.newPrice !== undefined && (
                <div className={s.item}>
                  <Body
                    size="uppercase-small"
                    weight="semiBold"
                    className={s.adult}
                  >
                    Взрослый
                  </Body>
                  <div className={s.values}>
                    <Heading level={5} tag="p">
                      {formatNumberWithSpaces(data.price.adult.newPrice)}
                    </Heading>
                    {data?.price?.adult?.oldPrice &&
                      data.price.adult.oldPrice >
                        data.price.adult.newPrice! && (
                        <Body
                          size="uppercase-small"
                          className={s.old}
                          weight="semiBold"
                        >
                          {formatNumberWithSpaces(data.price.adult.oldPrice)}
                        </Body>
                      )}
                  </div>
                </div>
              )}
            {data.price.childen &&
              typeof data.price.childen === "object" &&
              data.price.childen.newPrice !== null &&
              data.price.childen.newPrice !== undefined && (
                <div className={s.item}>
                  <Body
                    size="uppercase-small"
                    weight="semiBold"
                    className={s.adult}
                  >
                    <span>Детский</span>
                    <span>(6-13 лет)</span>
                  </Body>
                  <div className={s.values}>
                    <Heading level={5} tag="p">
                      {formatNumberWithSpaces(data.price.childen.newPrice)}
                    </Heading>
                    {data.price.childen.oldPrice &&
                      data.price.childen.oldPrice >
                        data.price.childen.newPrice! && (
                        <Body
                          className={s.old}
                          size="uppercase-small"
                          weight="semiBold"
                        >
                          {formatNumberWithSpaces(data.price.childen.oldPrice)}
                        </Body>
                      )}
                  </div>
                </div>
              )}
          </div>
          <ButtonWithIcon
            variant="primary-dark"
            iconFillColor="cadmium"
            iconStrokeColor="white"
            iconName="arrow"
            onClick={onBuyClick}
          >
            купить билет
          </ButtonWithIcon>
        </div>
      ) : null}
    </div>
  );
};

PopupContent.displayName = "PopupContent";
